/**
 * Feed service — all feed and post API calls.
 */
import {
  POST_MEDIA_TYPE,
  POST_IMAGE_LAYOUT,
  POST_FEELINGS,
  POST_PRIVACY,
  CommentContentType,
  PostContentType,
  STORAGE_KEYS,
} from '../../constants';
import { analytics, ANALYTICS_EVENTS } from '../../analytics';
import { GIPHY_CDN_BASE_URL } from '../../constants/giphy';
import type { CommentItem } from '../../types/comments';
import type {
  FeedPost,
  FeedPage,
  FeedTab,
  FeedFilterParams,
  CreatePostPayload,
  MutationResult,
  Profile,
  TranslateFn,
  MediaFile,
  PostImageLayout,
  PostPrivacy,
  GetUserPostsRequest,
  LikedUser,
} from '../../types';
import feedApi, { FeedApiResponsePost, type CommentApiResponse, type PostReactionUser } from '../feed/feedApi';
import { normalizeUserGuid, resolveProfileUserGuidFromRecord } from '../../utils/navigateToUserProfile';
import { formatContentTimestamp, getTimezoneOffsetMinutes } from '../../utils/dateHelpers';
import { detectAndDecode } from '../../utils/base64';
import {
  isCommentAttachmentPlaceholder,
  isCommentMediaSameAsAvatar,
} from '../../constants/commentMedia';
import { getFullImageUrl, resolveProfilePictureUrl, toRelativeMediaPath } from '../../utils/imageUrl';
import { buildFilePart } from '../../platform/filePart';
import {
  appendHashtagsToText,
  mapTaggedUsersToBuddies,
  normalizeHashtagList,
} from '../../utils/postDisplay';
import { resolveLayoutForAlbum } from '../../utils/postImageLayout';
import { formatWeightDisplay, formatWeightInUnit } from '../../utils/weightUtils';
import { getApiErrorMessage, UnusableResponseError } from '../../utils/apiErrorHandler';
import { parsePostFeelingTitle } from '../../utils/postFeeling';
import type { ApiError } from '../../types/api';

export const isAiAgentRecord = (record: any): boolean => {
  return !!(
    record.isAIAgent ||
    record.isAiAgent ||
    (record.contentTypeFor && String(record.contentTypeFor).toLowerCase().includes('ai')) ||
    (record.contentTypeName && String(record.contentTypeName).toLowerCase().includes('ai'))
  );
};

/** Reason text from a 200 response that reports its own failure. */
const readResponseMessage = (data: unknown): string | undefined => {
  if (!data || typeof data !== 'object') return undefined;
  const body = data as { message?: unknown; error?: unknown };
  const reason = typeof body.message === 'string' ? body.message : body.error;
  return typeof reason === 'string' && reason.trim() ? reason.trim() : undefined;
};

/**
 * Keep post IDs as digit strings (no Number()) so large .NET `long` values
 * are not corrupted past Number.MAX_SAFE_INTEGER.
 */
const normalizeNumericId = (
  value: string | number | null | undefined,
): string | null => {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!/^\d+$/.test(raw)) return null;
  // Reject zero — not a valid DB post id.
  if (/^0+$/.test(raw)) return null;
  return raw;
};

/**
 * `POST /Feeds/Post` answers `{ statusCode, message, success }` with no id, so a
 * created post is prefixed until the server copy is fetched. Such an id cannot
 * be sent back to the API (comments, reactions, save, delete all reject it).
 */
export const PENDING_POST_ID_PREFIX = 'pending-';

/** True when a post id is a real server id rather than an optimistic placeholder. */
export const isPersistedPostId = (
  id: string | number | null | undefined,
): boolean => normalizeNumericId(id) !== null;

/** Resolve created/updated post id from common API response shapes. */
const extractPostIdFromResponse = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') return null;
  const root = data as Record<string, unknown>;
  const nested =
    root.data && typeof root.data === 'object'
      ? (root.data as Record<string, unknown>)
      : null;
  const result =
    root.result && typeof root.result === 'object'
      ? (root.result as Record<string, unknown>)
      : null;

  const candidates = [
    root.postId,
    root.PostId,
    nested?.postId,
    nested?.PostId,
    result?.postId,
    result?.PostId,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeNumericId(candidate as string | number | null);
    if (normalized) return normalized;
  }
  return null;
};

const resolveFeedPostId = (post: FeedApiResponsePost): string => {
  const extended = post as FeedApiResponsePost & { PostId?: number | string };
  const raw = extended.postId ?? extended.PostId;
  return normalizeNumericId(raw) ?? String(raw ?? '');
};

const resolveProfileUserId = (profile: Profile | null): string | undefined => {
  if (profile?.userId != null) {
    return String(profile.userId);
  }
  if (profile?.id != null) {
    return String(profile.id);
  }
  return undefined;
};

const formatFeelingTitle = (feelingKey?: string | null): string => {
  if (!feelingKey) return '';
  const match = POST_FEELINGS.find(f => f.key === feelingKey);
  if (!match) return feelingKey;
  return `${match.emoji} ${match.key}`;
};

/**
 * Replaces backend-generated captions like "2 image" or "3 images" with
 * natural language ("Shared 2 photos"). If the text doesn't match the
 * auto-caption pattern, it passes through unchanged.
 */
const sanitizeAutoCaption = (text: string): string => {
  const match = text.trim().match(/^(\d+)\s*images?$/i);
  if (match) {
    const count = parseInt(match[1], 10);
    return `Shared ${count} ${count === 1 ? 'photo' : 'photos'}`;
  }
  return text;
};

const buildOptimisticFeedPost = (
  id: string,
  payload: CreatePostPayload,
  profile: Profile | null,
): FeedPost => {
  const text = (payload.content || '').trim();
  const mediaFiles = resolvePayloadMediaFiles(payload);
  const primary = mediaFiles[0] || payload.media || null;
  const isVideo = primary?.mediaType === POST_MEDIA_TYPE.VIDEO;
  const mediaUri = primary?.uri;
  const imageUris =
    !isVideo && mediaFiles.length > 1
      ? mediaFiles.map(file => file.uri).filter(Boolean)
      : undefined;
  const imageLayout = isVideo
    ? null
    : resolveLayoutForAlbum(
        imageUris?.length ?? (mediaUri ? 1 : 0),
        payload.imageLayout ?? null,
      );

  const createdAt = new Date();

  return {
    id,
    userId: resolveProfileUserId(profile),
    username: profile?.name || 'User',
    avatar: profile?.avatar || undefined,
    timeAgo: formatContentTimestamp(createdAt),
    postDate: createdAt.toISOString(),
    text,
    content: text,
    feeling: payload.feeling || null,
    hashtags: normalizeHashtagList(payload.hashtags),
    taggedBuddies: payload.taggedBuddyDetails || [],
    privacy: payload.privacy,
    postVisibility: mapPrivacyToVisibilityInt(payload.privacy),
    likes: 0,
    comments: 0,
    shares: 0,
    saved: false,
    liked: false,
    groupId: payload.groupId,
    groupName: payload.groupName,
    image: !isVideo && mediaUri ? { uri: mediaUri } : undefined,
    images: imageUris?.map(uri => ({ uri })),
    imageLayout,
    video: isVideo && mediaUri ? { uri: mediaUri } : undefined,
    allowShare: profile?.allowSharing !== false,
  };
};

const hasReactionValue = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  if (typeof value !== 'string') return false;

  const normalized = value.trim().toLowerCase();
  if (!normalized) return false;
  const numericValue = Number(normalized);
  if (Number.isFinite(numericValue)) return numericValue > 0;
  return normalized === 'true' || normalized === 'yes';
};

const hasReactionName = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  return normalized !== '' &&
    normalized !== 'none' &&
    normalized !== 'null' &&
    normalized !== 'false' &&
    normalized !== 'no reaction';
};

/** Normalizes the reaction variants returned by feed and profile-post reads. */
export const hasCurrentUserReacted = (
  post: Pick<FeedApiResponsePost, 'isReacted' | 'reactionName'>,
  useReactionNameFallback = false,
): boolean =>
  hasReactionValue(post.isReacted) ||
  (useReactionNameFallback && hasReactionName(post.reactionName));

// Helper to map API response to FeedPost
export const mapFeedApiResponse = (
  post: FeedApiResponsePost,
  options?: { useReactionNameFallback?: boolean },
): FeedPost => {
  const avatarUrl = resolveProfilePictureUrl(post.userPhoto);
  const contentType = (post.contentTypeName || '').toLowerCase().trim();
  const isVideo = contentType === 'video';
  const isGif = contentType === 'gif';

  let mediaUri: string | undefined;
  let imageUris: string[] | undefined;
  if (Array.isArray(post.photoAlbum) && post.photoAlbum.length > 0) {
    imageUris = post.photoAlbum
      .map(item => getFullImageUrl(item) || item)
      .filter((item): item is string => !!item);
    mediaUri = imageUris[0];
  } else if (post.url) {
    // GIF posts: backend URL is final — do not prepend or transform.
    mediaUri = isGif ? post.url.trim() : (getFullImageUrl(post.url) || post.url);
  } else if (post.bodyText && (post.bodyText.startsWith('/UserImages/') || post.bodyText.startsWith('UserImages/'))) {
    mediaUri = getFullImageUrl(post.bodyText);
  }

  // GetFeeds answers with the album but no layout, so the count decides it the
  // same way the composer did on the way up. An album of 3+ is a gallery: without
  // this it came back as `Single` and the feed showed one photo of the set.
  const imageLayout: PostImageLayout | null = isVideo
    ? null
    : resolveLayoutForAlbum(imageUris?.length ?? (mediaUri ? 1 : 0), null);

  const rawBody = sanitizeAutoCaption(detectAndDecode(post.bodyText || ''));
  const hashtags = normalizeHashtagList(post.hashTags);
  const fullText = appendHashtagsToText(rawBody, hashtags);

  return {
    id: resolveFeedPostId(post),
    // GetUserPosts / group feeds spell the author GUID differently to GetFeeds,
    // and a numeric-only value is not a GUID the profile route can open.
    profileUserGuid:
      resolveProfileUserGuidFromRecord(post as unknown as Record<string, unknown>) ?? undefined,
    userId: String(post.userId),
    username: post.userName || 'User',
    firstName: post.firstName || null,
    currentWeight: post.currentWeight != null
      ? formatWeightInUnit(Number(post.currentWeight), post.scaleUnitWeightName)
      : undefined,
    // Feed reads send timezoneOffset. Naive postDate strings are device-local
    // wall-clock (not UTC), so they are read as local.
    timeAgo: formatContentTimestamp(post.postDate, 'local') || post.postDate,
    postDate: post.postDate,
    text: fullText,
    content: rawBody,
    feeling: parsePostFeelingTitle(post.title),
    // The API echoes tags back either bare or `#`-prefixed; every reader (chips,
    // filters, the edit composer) expects the bare label.
    hashtags,
    taggedBuddies: mapTaggedUsersToBuddies((post as any).taggedUsers || (post as any).taggedBuddies || (post as any).taggedBuddyDetails),
    privacy: mapVisibilityIntToPrivacy(
      post.postVisibility ??
        (post as unknown as { privacy?: string | number }).privacy ??
        (post as unknown as { privacyId?: string | number }).privacyId,
    ),
    postVisibility: typeof post.postVisibility === 'number'
      ? post.postVisibility
      : mapPrivacyToVisibilityInt(
          (post as unknown as { privacy?: string | number }).privacy ??
            (post as unknown as { privacyId?: string | number }).privacyId,
        ),
    likes: post.reactedCount || 0,
    comments: post.commentCount || 0,
    shares: post.totalShares || 0,
    saved: !!post.isSaved,
    liked: hasCurrentUserReacted(post, options?.useReactionNameFallback),
    isBlocked: !!post.isBlocked,
    avatar: avatarUrl,
    image: !isVideo && mediaUri ? { uri: mediaUri } : undefined,
    images:
      !isVideo && imageUris && imageUris.length > 1
        ? imageUris.map(uri => ({ uri }))
        : undefined,
    imageLayout,
    video: isVideo && mediaUri ? { uri: mediaUri } : undefined,
    thumbnail: isVideo && post.thumbnailUrl ? (getFullImageUrl(post.thumbnailUrl) || post.thumbnailUrl) : undefined,
    groupId: post.groupId ? String(post.groupId) : undefined,
    groupName: post.groupName || undefined,
    allowShare: post.allowShare !== false,
    isAIBuddy: isAiAgentRecord(post),
    isBlog:
      typeof post.isBlog === 'boolean'
        ? post.isBlog
        : typeof (post as any).IsBlog === 'boolean'
        ? (post as any).IsBlog
        : (post as any).isBlog === 1 || (post as any).IsBlog === 1
        ? true
        : (post as any).isBlog === 0 || (post as any).IsBlog === 0
        ? false
        : undefined,
  };
};

export const mapVisibilityIntToPrivacy = (
  visibility?: number | string | null,
): PostPrivacy => {
  if (visibility == null) return POST_PRIVACY.PUBLIC as PostPrivacy;
  if (typeof visibility === 'number') {
    if (visibility === 2) return POST_PRIVACY.BUDDIES_ONLY as PostPrivacy;
    if (visibility === 3) return POST_PRIVACY.ONLY_ME as PostPrivacy;
    return POST_PRIVACY.PUBLIC as PostPrivacy;
  }
  const checkVisibility = String(visibility).toLowerCase().trim();
  if (checkVisibility === '2' || checkVisibility === 'buddies_only' || checkVisibility.includes('buddies')) {
    return POST_PRIVACY.BUDDIES_ONLY as PostPrivacy;
  }
  if (checkVisibility === '3' || checkVisibility === 'only_me' || checkVisibility.includes('only') || checkVisibility.includes('me')) {
    return POST_PRIVACY.ONLY_ME as PostPrivacy;
  }
  return POST_PRIVACY.PUBLIC as PostPrivacy;
};

export const mapPrivacyToVisibilityInt = (
  privacy?: string | number | null,
): number => {
  if (typeof privacy === 'number') return privacy;
  if (!privacy) return 1;
  const p = String(privacy).toLowerCase().trim();
  if (p.includes('buddies') || p === '2') return 2;
  if (p.includes('only') || p.includes('me') || p === '3') return 3;
  return 1;
};

const mapContentType = (
  media?: MediaFile | null,
): { name: string; id: PostContentType } => {
  if (!media || !media.uri) {
    return { name: 'Text', id: PostContentType.Text };
  }
  if (media.mediaType === POST_MEDIA_TYPE.VIDEO) {
    return { name: 'Video', id: PostContentType.Video };
  }
  if (
    media.mediaType === POST_MEDIA_TYPE.GIF ||
    (typeof media.uri === 'string' && media.uri.toLowerCase().endsWith('.gif')) ||
    (media.type || '').toLowerCase().includes('gif')
  ) {
    return { name: 'Gif', id: PostContentType.Gif };
  }
  // Backend expects "Image" (not "Photo") — "Incorrect Content Type." otherwise.
  // Comparison2/4 stay ContentType=Image; layout is sent via MetadataJson + Files.
  return { name: 'Image', id: PostContentType.Image };
};

const resolvePayloadMediaFiles = (payload: CreatePostPayload): MediaFile[] => {
  if (Array.isArray(payload.mediaFiles) && payload.mediaFiles.length > 0) {
    return payload.mediaFiles.filter(file => !!file?.uri);
  }
  if (payload.media?.uri) {
    return [payload.media];
  }
  return [];
};

const resolveImageLayout = (
  payload: CreatePostPayload,
  mediaFiles: MediaFile[],
): PostImageLayout | null => {
  const primary = mediaFiles[0];
  if (!primary) return null;
  if (
    primary.mediaType === POST_MEDIA_TYPE.VIDEO ||
    primary.mediaType === POST_MEDIA_TYPE.GIF
  ) {
    return null;
  }
  return resolveLayoutForAlbum(mediaFiles.length, payload.imageLayout ?? null);
};

const buildPostMetadataJson = (
  layout: PostImageLayout | null,
  mediaFiles: MediaFile[],
): string | null => {
  if (!layout) return null;
  return JSON.stringify({
    layout,
    imageCount: mediaFiles.length,
  });
};

const resolveUploadFileMeta = (
  media: MediaFile,
): { uri: string; type: string; name: string } => {
  const uri = media.uri;
  const isVideo = media.mediaType === POST_MEDIA_TYPE.VIDEO;
  const mime = (media.type || '').toLowerCase();

  if (isVideo) {
    const ext =
      mime.includes('quicktime') || uri.toLowerCase().endsWith('.mov')
        ? 'mov'
        : mime.includes('mpeg')
          ? 'mpeg'
          : 'mp4';
    return {
      uri,
      type: mime && mime.startsWith('video/') ? mime : 'video/mp4',
      name: media.fileName || `upload.${ext}`,
    };
  }

  const isGif =
    media.mediaType === POST_MEDIA_TYPE.GIF ||
    mime.includes('gif') ||
    uri.toLowerCase().endsWith('.gif');

  if (isGif) {
    return {
      uri,
      type: 'image/gif',
      name: media.fileName || 'upload.gif',
    };
  }

  return {
    uri,
    type: mime && mime.startsWith('image/') ? mime : 'image/jpeg',
    name: media.fileName || 'upload.jpg',
  };
};

const isGifMediaFile = (media?: MediaFile | null): boolean =>
  !!media &&
  (media.mediaType === POST_MEDIA_TYPE.GIF ||
    (typeof media.uri === 'string' && media.uri.toLowerCase().endsWith('.gif')) ||
    (media.type || '').toLowerCase().includes('gif'));

/** Formats a remote GIF URL/path for Feeds/Post `MediaUrl` (GIF posts only). */
const formatGifPostMediaUrl = (uri: string): string => {
  const trimmed = uri.trim();
  if (!trimmed) return trimmed;

  const cdnBase = GIPHY_CDN_BASE_URL.replace(/\/+$/, '');
  if (trimmed.toLowerCase().startsWith(cdnBase.toLowerCase())) {
    return trimmed;
  }

  let pathPart = trimmed;
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      pathPart = decodeURIComponent(new URL(trimmed).pathname).replace(/^\/+/, '');
    } catch {
      return trimmed;
    }
  } else {
    pathPart = trimmed.replace(/^\/+/, '');
  }

  return `${cdnBase}/${pathPart}`;
};

const isLocalMediaUri = (uri?: string | null): boolean => {
  if (!uri || typeof uri !== 'string') return false;
  const normalized = uri.trim().toLowerCase();
  return (
    normalized.startsWith('file:') ||
    normalized.startsWith('content:') ||
    normalized.startsWith('ph:') ||
    normalized.startsWith('assets-library:') ||
    // Android document / raw paths without a scheme
    (normalized.startsWith('/') && !normalized.startsWith('//'))
  );
};

const isRemoteMediaUri = (uri?: string | null): boolean => {
  if (!uri || typeof uri !== 'string') return false;
  const normalized = uri.trim().toLowerCase();
  return normalized.startsWith('http://') || normalized.startsWith('https://');
};

/** Browser object/data URL produced by a picked File — never sendable as a URL. */
const isBrowserObjectUri = (uri?: string | null): boolean => {
  if (!uri || typeof uri !== 'string') return false;
  const normalized = uri.trim().toLowerCase();
  return normalized.startsWith('blob:') || normalized.startsWith('data:');
};

/** Web only: the picked File/Blob browsers require in FormData. */
const resolveMediaBlob = (media?: MediaFile | null): Blob | null => {
  const picked = media?.file;
  return typeof Blob !== 'undefined' && picked instanceof Blob ? picked : null;
};

/** Absolute CDN URL or server-relative path returned by FileUpload. */
const SERVER_MEDIA_FOLDER_RE =
  /^(?:\/)?(?:UserImages|GroupImages|ConversationImages|Conversations|WLBImages|PostImages)\//i;

const isServerMediaUrl = (uri?: string | null): boolean => {
  if (!uri || typeof uri !== 'string') return false;
  const trimmed = uri.trim();
  if (isRemoteMediaUri(trimmed)) return true;
  return SERVER_MEDIA_FOLDER_RE.test(trimmed.replace(/\\/g, '/'));
};

/**
 * A picked file we can stream as multipart: a native local URI, or a browser
 * object URL that still carries its File — a blob URL alone cannot be uploaded.
 */
const isUploadableLocalMedia = (media?: MediaFile | null): boolean => {
  const uri = media?.uri?.trim();
  if (!uri || isServerMediaUrl(uri)) return false;
  if (isBrowserObjectUri(uri)) return !!resolveMediaBlob(media);
  return isLocalMediaUri(uri);
};

/** image-crop-picker returns video duration in milliseconds. */
const resolveVideoDurationSeconds = (media?: MediaFile | null): number | null => {
  if (!media || media.mediaType !== POST_MEDIA_TYPE.VIDEO) return null;
  const raw = media.duration;
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return null;
  const seconds = raw >= 1000 ? Math.round(raw / 1000) : Math.round(raw);
  return seconds > 0 ? seconds : null;
};

/** Thrown when tagged buddies cannot be mapped to numeric BuddiesTags ids. */
export class BuddiesTagsUnresolvedError extends Error {
  constructor() {
    super('BUDDIES_TAGS_UNRESOLVED');
    this.name = 'BuddiesTagsUnresolvedError';
  }
}

/** BuddiesTags accepts numeric userIds only — never profileUserGuid strings. */
const extractNumericBuddyTagId = (buddy: unknown): string => {
  const raw =
    typeof buddy === 'object' && buddy !== null
      ? (buddy as { id?: unknown; userId?: unknown }).id
        ?? (buddy as { userId?: unknown }).userId
        ?? ''
      : buddy;
  const trimmed = String(raw ?? '').trim();
  return /^\d+$/.test(trimmed) ? trimmed : '';
};

const buildBuddiesTags = (buddyDetails: unknown[]): string =>
  buddyDetails
    .map(extractNumericBuddyTagId)
    .filter(id => id !== '')
    .join(',');

const buildPostFormData = (
  payload: CreatePostPayload,
  postId?: string,
): FormData => {
  const formData = new FormData();

  if (postId) {
    const normalizedPostId = normalizeNumericId(postId);
    if (normalizedPostId) {
      // Always send as digit string — avoids JS Number precision loss on long IDs.
      formData.append('PostId', normalizedPostId);
    } else {
      console.warn('[Feeds/Post] Skipping invalid PostId:', postId);
    }
  }

  // Feeling is persisted in Title (API has no dedicated Feeling field).
  formData.append('Title', formatFeelingTitle(payload.feeling));
  formData.append('BodyText', (payload.content || '').trim());
  formData.append('ExternalUrl', '');

  const visibility = mapPrivacyToVisibilityInt(payload.privacy);
  formData.append('PostVisibility', String(visibility));
  formData.append('PrivacyId', String(visibility));

  // HashTags carries bare labels; the `#` belongs to the display form only.
  formData.append('HashTags', normalizeHashtagList(payload.hashtags).join(','));

  const buddyDetails = payload.taggedBuddyDetails || payload.taggedBuddies || [];
  const buddiesTags = buildBuddiesTags(buddyDetails);

  if (!payload.omitBuddiesTags) {
    if (buddyDetails.length > 0 && buddiesTags === '') {
      throw new BuddiesTagsUnresolvedError();
    }
    formData.append('BuddiesTags', buddiesTags);
  }

  formData.append('AllowComments', 'true');
  formData.append('AllowShare', 'true');

  // Device offset (minutes east of UTC, DST-aware) so the backend can store UTC.
  formData.append(
    'TimezoneOffset',
    String(payload.timezoneOffset ?? getTimezoneOffsetMinutes()),
  );

  if (payload.groupId) {
    const numGroupId = Number(payload.groupId);
    if (!isNaN(numGroupId) && numGroupId > 0) {
      formData.append('GroupId', String(numGroupId));
    }
  }

  const mediaFiles = resolvePayloadMediaFiles(payload);
  const primaryMedia = mediaFiles[0] || payload.media || null;
  const contentType = mapContentType(primaryMedia);
  formData.append('ContentType', contentType.name);
  formData.append('ContentTypeId', String(contentType.id));

  const imageLayout = resolveImageLayout(payload, mediaFiles);
  const metadataJson = buildPostMetadataJson(imageLayout, mediaFiles);
  if (metadataJson) {
    formData.append('MetadataJson', metadataJson);
  }

  const remoteUrls = mediaFiles
    .filter(file => isServerMediaUrl(file.uri?.trim() || ''))
    .map(file => {
      const uri = file.uri!.trim();
      if (isGifMediaFile(file)) {
        return formatGifPostMediaUrl(uri);
      }
      return toRelativeMediaPath(uri) || uri;
    });
  const localFiles = mediaFiles.filter(isUploadableLocalMedia);

  // Pre-uploaded URLs (Giphy / keep-existing / compare existing images).
  // On edit (postId set) of a single-image post with no file changes, the API
  // preserves existing media when MediaUrl is omitted. When changing to compare
  // layout, or adding new files/multiple images, remoteUrls must be included.
  const shouldAppendMediaUrls =
    remoteUrls.length > 0 &&
    (!postId ||
      localFiles.length > 0 ||
      imageLayout === POST_IMAGE_LAYOUT.COMPARISON_2 ||
      imageLayout === POST_IMAGE_LAYOUT.COMPARISON_4 ||
      mediaFiles.length > 1 ||
      (primaryMedia != null && isGifMediaFile(primaryMedia)));

  if (shouldAppendMediaUrls) {
    // Swagger: MediaUrl is string[] — append each entry for proper array binding.
    remoteUrls.forEach(url => {
      formData.append('MediaUrl', url);
    });
  }

  const durationSeconds = resolveVideoDurationSeconds(primaryMedia);
  if (durationSeconds != null) {
    formData.append('DurationSeconds', String(durationSeconds));
  } else if (contentType.id !== PostContentType.Text) {
    // Duration is only relevant for media posts; omit for Text to avoid unused media rows.
    formData.append('DurationSeconds', '0');
  }

  // Append each local file in order (Single / Comparison2 / Comparison4 / album).
  // Swagger: Files is array — same field name repeated. Also keep legacy `Media`.
  localFiles.forEach((file, index) => {
    const meta = resolveUploadFileMeta(file);
    const part = buildFilePart({ ...meta, blob: resolveMediaBlob(file) });
    formData.append('Files', part);
    if (index === 0) {
      formData.append('Media', part);
    }
  });

  return formData;
};

export const mapComments = (rawComments: CommentApiResponse[]): CommentItem[] => {
  const commentMap = new Map<number, CommentItem>();
  
  // First pass: create all CommentItem objects
  rawComments.forEach((c) => {
    const extra = c as CommentApiResponse & {
      url?: string;
      createdAt?: string;
      dateCreated?: string;
      commentedAt?: string;
    };
    const mediaPath =
      Array.isArray(extra.mediaUrl) && extra.mediaUrl.length > 0
        ? extra.mediaUrl[0]
        : typeof extra.url === 'string'
          ? extra.url
          : undefined;
    const relativeMediaUrl =
      typeof mediaPath === 'string' && mediaPath.trim()
        ? mediaPath.trim().replace(/\\/g, '/')
        : undefined;
    const omitAttachment =
      isCommentAttachmentPlaceholder(mediaPath) ||
      isCommentMediaSameAsAvatar(mediaPath, extra.userPhoto);
    const attachmentUrl = omitAttachment
      ? undefined
      : getFullImageUrl(mediaPath) || undefined;
    const isReacted =
      typeof extra.isReacted === 'boolean'
        ? extra.isReacted
        : Number(extra.isReacted) > 0;
    const isBlocked =
      typeof extra.isBlocked === 'boolean'
        ? extra.isBlocked
        : Number(extra.isBlocked) > 0;
    const rawCommentDate =
      extra.commentDate ?? extra.createdAt ?? extra.dateCreated ?? extra.commentedAt;

    commentMap.set(extra.postCommentId, {
      id: String(extra.postCommentId),
      postCommentId: extra.postCommentId,
      username: extra.userName || 'Unknown User',
      firstName: extra.firstName || null,
      avatar:
        resolveProfilePictureUrl(
          extra.userPhoto ||
            (extra as any).userPicture ||
            (extra as any).profileImage ||
            (extra as any).avatar ||
            (extra as any).photo,
        ) || '',
      image: attachmentUrl,
      mediaUrl: omitAttachment ? undefined : relativeMediaUrl,
      comment: detectAndDecode(extra.bodyText || ''),
      createdAt: formatContentTimestamp(rawCommentDate, 'local'),
      commentDate: typeof rawCommentDate === 'string' ? rawCommentDate : undefined,
      likeCount: extra.reactedCount || 0,
      isLiked: isReacted,
      authorId: String(extra.userId),
      profileUserGuid: resolveProfileUserGuidFromRecord(
        extra as unknown as Record<string, unknown>,
      ),
      isBlocked,
      isAIBuddy: isAiAgentRecord(extra),
      replies: [],
    });
  });

  const parentById = new Map<number, number>();
  rawComments.forEach((c: CommentApiResponse) => {
    const parentId = c.parentCommentId;
    const hasParent =
      parentId != null && Number(parentId) !== 0 && !Number.isNaN(Number(parentId));
    if (hasParent) parentById.set(c.postCommentId, Number(parentId));
  });

  const findThreadRootId = (id: number): number => {
    const seen = new Set<number>();
    let current = id;
    while (true) {
      const parentId = parentById.get(current);
      if (parentId == null || !commentMap.has(parentId) || seen.has(current)) {
        return current;
      }
      seen.add(current);
      current = parentId;
    }
  };

  const rootComments: CommentItem[] = [];

  // Second pass: attach every reply to its thread root. The UI is two-level
  // (root + replies), so nesting under another reply would hide those rows
  // while they still count toward API commentCount.
  rawComments.forEach((c: CommentApiResponse) => {
    const item = commentMap.get(c.postCommentId);
    if (!item) return;

    const parentId = c.parentCommentId;
    const hasParent =
      parentId != null && Number(parentId) !== 0 && !Number.isNaN(Number(parentId));

    if (hasParent) {
      const parent = commentMap.get(Number(parentId));
      if (parent) {
        item.replyTo = parent.username;
        const root = commentMap.get(findThreadRootId(c.postCommentId));
        if (root && root !== item) {
          root.replies = root.replies ?? [];
          root.replies.push(item);
        } else {
          rootComments.push(item);
        }
      } else {
        rootComments.push(item);
      }
    } else {
      rootComments.push(item);
    }
  });

  return rootComments;
};

/** Maps GetPostReactionList rows to UI-friendly liked-user entries. */
export const mapPostReactionUserToLikedUser = (
  user: PostReactionUser,
): LikedUser => {
  let currentWeight: string | undefined;
  const weightVal = user.currentWeight ?? user.weight;
  if (weightVal != null && weightVal !== '') {
    const formatted = formatWeightDisplay(
      Number(weightVal),
      user.scaleUnitWeightName ?? undefined,
    );
    if (formatted) {
      currentWeight = `CW: ${formatted}`;
    }
  }

  const profileGuid = normalizeUserGuid(user.profileUserGuid);
  const alternateUserGuid = normalizeUserGuid(user.userGuid);

  return {
    id: String(user.userId),
    username: user.userName || 'User',
    firstName: user.firstName || null,
    name: user.userName || 'User',
    profileUserGuid: profileGuid ?? alternateUserGuid ?? null,
    userGuid: alternateUserGuid ?? null,
    avatar: resolveProfilePictureUrl(user.userPhoto),
    isBuddy: !!user.isBuddy,
    isFollowing: user.isFollowing ?? !!user.isBuddy,
    currentWeight,
  };
};

export const mapPostReactionUsersToLikedUsers = (
  users: PostReactionUser[],
): LikedUser[] => users.map(mapPostReactionUserToLikedUser);

const feedService = {
  getUserPosts: (payload: GetUserPostsRequest) =>
    feedApi.getUserPosts(payload),
  
  /**
   * @param searchQuery Optional filter passed verbatim to the GetFeeds `search` field.
   *   Accepts a single hashtag (`"#fitness"`) or a comma-separated list
   *   (`"#fitness,#weightloss,#healthy"`). Omit or pass `undefined` for no filter.
   */
  getFeed: async (tab: FeedTab, page = 1, searchQuery?: string): Promise<FeedPage> => {
    try {
      const pageSize = 10;
      // Map tab name to proper method capitalization
      let methodStr = tab.charAt(0).toUpperCase() + tab.slice(1);
      if (tab.toLowerCase() === 'wlb') {
        methodStr = 'WLB';
      }
      
      const response = await feedApi.getFeeds({
        method: methodStr,
        startIndex: page,
        pageSize,
        ...(searchQuery !== undefined ? { search: searchQuery } : {}),
      });

      if (response.status === 200 && response.data?.success) {
        const rawFeeds = Array.isArray(response.data.feeds) ? response.data.feeds : [];
        const data = rawFeeds.map(post => mapFeedApiResponse(post));
        const hasNextPage = data.length === pageSize;
        return { data, hasNextPage, page };
      }
      throw new UnusableResponseError('GetFeeds');
    } catch (err) {
      // Raised, never swallowed into an empty page: the caller has to be able to
      // tell "no posts yet" apart from "the request failed".
      console.error('[feedService] Error in getFeed:', err);
      throw err;
    }
  },

  filterPosts: async (filters: FeedFilterParams, page = 1): Promise<{ success: boolean; data?: FeedPost[]; hasNextPage?: boolean; page?: number }> => {
    try {
      const pageSize = 10;
      // FilterPostsRequest is additionalProperties: false and has no timezoneOffset.
      const response = await feedApi.filterPosts({
        ...filters,
        startIndex: page,
        pageSize,
      });

      if (response.status === 200 && response.data?.success !== false) {
        const rawFeeds = Array.isArray(response.data.feeds) ? response.data.feeds : [];
        const data = rawFeeds.map(post => mapFeedApiResponse(post));
        const hasNextPage = data.length === pageSize;
        return { success: true, data, hasNextPage, page };
      }
      return { success: false, data: [], hasNextPage: false, page };
    } catch (err) {
      // A transport failure is not "no matches": rethrow so the caller reports it.
      console.error('[feedService] Error in filterPosts:', err);
      throw err;
    }
  },

  getBlogFeed: async (page = 1): Promise<FeedPage> => {
    try {
      const pageSize = 10;
      const response = await feedApi.getFeeds({
        method: 'Blogs',
        startIndex: page,
        pageSize,
      });

      if (response.status === 200 && response.data?.success) {
        const rawFeeds = Array.isArray(response.data.feeds) ? response.data.feeds : [];
        // Every row of this method is a blog, whether or not the payload says so.
        const data = rawFeeds.map(feed => ({ ...mapFeedApiResponse(feed), isBlog: true }));
        const hasNextPage = data.length === pageSize;
        return { data, hasNextPage, page };
      }
      throw new UnusableResponseError('GetFeeds(Blogs)');
    } catch (err) {
      // Rethrown so callers can tell "no blog posts" apart from "request failed".
      console.error('[feedService] Error in getBlogFeed:', err);
      throw err;
    }
  },

  likePost: async (
    postId: string,
    options?: { currentlyLiked?: boolean },
  ): Promise<MutationResult> => {
    try {
      const response = await feedApi.postReaction({
        postId: Number(postId),
        reactionTypeId: 1, // Standard Like reaction type
      });
      if (response.status === 200 && response.data?.success) {
        // Only count becoming liked — not an unlike toggle.
        if (options?.currentlyLiked === false) {
          analytics.trackEvent('post_liked', { source: 'feed' });
          analytics.trackOnce(
            STORAGE_KEYS.ANALYTICS_FIRST_LIKE,
            ANALYTICS_EVENTS.ACTIVATION_FIRST_LIKE,
          );
        }
        return { success: true, postId };
      }
      return { success: false, postId };
    } catch (err) {
      console.error('[feedService] Error in likePost:', err);
      return { success: false, postId };
    }
  },

  sharePost: async (postId: string, bodyText?: string, groupId?: string): Promise<MutationResult> => {
    try {
      const numericPostId = Number(postId);
      const numericGroupId = groupId ? Number(groupId) : undefined;
      const response = await feedApi.sharePost({
        RootPostId: numericPostId,
        groupId: numericGroupId,
        bodyText,
      });
      if (response.status === 200 || response.status === 201 || response.data?.success !== false) {
        return { success: true, postId };
      }
      return { success: false, postId };
    } catch (err) {
      console.error('[feedService] Error in sharePost:', err);
      return { success: false, postId };
    }
  },

  reportPost: async (params: {
    postId?: string | number | null;
    reasonIds?: number[];
    otherDescription?: string;
    commentId?: string | number | null;
    reportId?: number | null;
  }): Promise<MutationResult> => {
    try {
      const hasReasons = Array.isArray(params.reasonIds) && params.reasonIds.length > 0;
      const hasOther = typeof params.otherDescription === 'string' && params.otherDescription.trim().length > 0;

      if (!hasReasons && !hasOther) {
        console.error('[feedService] reportPost requires reasonIds or otherDescription', {
          reasonIds: params.reasonIds,
          postId: params.postId,
        });
        return { success: false };
      }

      const numericPostId =
        params.postId != null && String(params.postId).trim() !== ''
          ? Number(params.postId)
          : null;
      const numericCommentId =
        params.commentId != null && String(params.commentId).trim() !== ''
          ? Number(params.commentId)
          : null;
      const numericReportId =
        params.reportId != null ? Number(params.reportId) : 0;

      const payload: import('../feed/feedApi').ReportPostRequest = {
        reportId: Number.isFinite(numericReportId) ? numericReportId : 0,
        postId: Number.isFinite(numericPostId as number) ? numericPostId : null,
        commentId: Number.isFinite(numericCommentId as number)
          ? numericCommentId
          : null,
        reviewStatus: 1,
      };

      if (hasReasons) {
        payload.reasonIds = params.reasonIds;
      }
      if (hasOther) {
        payload.otherDescription = params.otherDescription?.trim();
      }

      const response = await feedApi.reportPost(payload);
      if (response.status === 200 || response.status === 201 || response.data?.success !== false) {
        return { success: true };
      }
      return { success: false };
    } catch (err) {
      console.error('[feedService] Error in reportPost:', err);
      return { success: false };
    }
  },

  savePost: async (postId: string, isSave: boolean = true): Promise<MutationResult> => {
    try {
      const numericPostId = Number(postId);
      const response = await feedApi.saveUnsavePost({
        postId: numericPostId,
        isSave,
      });
      if (response.status === 200 && response.data?.success !== false) {
        return { success: true, postId };
      }
      return { success: false, postId };
    } catch (err) {
      console.error('[feedService] Error in savePost:', err);
      return { success: false, postId };
    }
  },

  getSavedPosts: async (page = 1, pageSize = 10): Promise<FeedPage> => {
    try {
      const response = await feedApi.getSavedPosts({
        startIndex: page,
        pageSize,
      });

      if (response.status === 200 && response.data?.success !== false) {
        const rawFeeds = Array.isArray(response.data.feeds) ? response.data.feeds : [];
        const data = rawFeeds.map((post) => ({
          ...mapFeedApiResponse(post),
          saved: true,
        }));
        const hasNextPage = data.length === pageSize;
        return { data, hasNextPage, page };
      }
      throw new UnusableResponseError('GetSavedPosts');
    } catch (err) {
      // Rethrown so an unreachable API is not shown as "no saved posts".
      console.error('[feedService] Error in getSavedPosts:', err);
      throw err;
    }
  },

  createComment: async (postId: string, text: string, parentId?: string, media?: MediaFile | null): Promise<MutationResult<{ commentId?: string }>> => {
    try {
      const numericPostId = Number(postId);
      if (isNaN(numericPostId) || numericPostId <= 0) {
        console.warn('[feedService] createComment called with invalid postId:', postId);
        return { success: false };
      }

      const formData = new FormData();
      formData.append('PostId', String(numericPostId));

      // Only send ParentCommentId for replies. Sending 0 is treated as "provided"
      // by the API and fails with "must be greater than zero when provided".
      const numericParentId = parentId != null ? Number(parentId) : NaN;
      if (!isNaN(numericParentId) && numericParentId > 0) {
        formData.append('ParentCommentId', String(numericParentId));
      }

      let contentTypeId = CommentContentType.Text;
      const mediaUri = media?.uri?.trim() || '';
      const hasLocalFile = !!mediaUri && isLocalMediaUri(mediaUri);
      const hasRemoteMedia = !!mediaUri && isRemoteMediaUri(mediaUri);
      if (mediaUri) {
        if (
          media?.mediaType === POST_MEDIA_TYPE.GIF ||
          mediaUri.toLowerCase().endsWith('.gif') ||
          media?.type?.includes('gif')
        ) {
          contentTypeId = CommentContentType.Gif;
        } else {
          contentTypeId = CommentContentType.Image;
        }
      }
      formData.append('ContentTypeId', String(contentTypeId));
      formData.append('BodyText', text || '');
      // Remote GIFs (Giphy) must use MediaUrl; empty MediaUrl binds as NULL on insert.
      if (hasRemoteMedia) {
        formData.append('MediaUrl', mediaUri);
      }
      formData.append('DurationSeconds', '0');

      if (hasLocalFile) {
        formData.append('Files', {
          uri: mediaUri,
          type: media?.type || 'image/jpeg',
          name: media?.fileName || 'upload.jpg',
        } as any);
      }

      const response = await feedApi.createComment(formData);
      if (
        (response.status === 201 || response.status === 200) &&
        response.data?.success !== false
      ) {
        const body = response.data as any;
        const dataNode = body?.data;
        const idRaw =
          dataNode?.postCommentId ??
          dataNode?.commentId ??
          dataNode?.id ??
          (typeof dataNode === 'number' || typeof dataNode === 'string' ? dataNode : undefined) ??
          body?.postCommentId ??
          body?.commentId ??
          body?.id;
        const parsed = idRaw != null && idRaw !== '' ? Number(idRaw) : NaN;
        const commentId =
          !isNaN(parsed) && parsed > 0 ? String(parsed) : undefined;
        analytics.trackEvent('comment_created', {
          post_type:
            contentTypeId === CommentContentType.Gif
              ? 'gif'
              : contentTypeId === CommentContentType.Image
                ? 'image'
                : 'text',
          source: parentId ? 'reply' : 'post',
        });
        analytics.trackOnce(
          STORAGE_KEYS.ANALYTICS_FIRST_COMMENT,
          ANALYTICS_EVENTS.ACTIVATION_FIRST_COMMENT,
        );
        return { success: true, data: { commentId } };
      }
      return { success: false };
    } catch (err: any) {
      console.error('[feedService] Error in createComment:', err.response?.data || err.message);
      return { success: false };
    }
  },

  editComment: async (
    commentId: string | number,
    text: string,
    mediaOptions?: {
      /** Relative/existing media path to keep when not replacing or removing. */
      existingMediaUrl?: string | null;
      /** Newly picked local file to upload, or remote GIF (Giphy) URL via MediaUrl. */
      media?: MediaFile | null;
      /** Explicitly strip media and convert comment to text-only. */
      removeMedia?: boolean;
    },
  ): Promise<MutationResult> => {
    try {
      const numCommentId = typeof commentId === 'number' ? commentId : Number(commentId);
      if (isNaN(numCommentId) || numCommentId <= 0) {
        console.warn('[feedService] editComment called with invalid non-numeric commentId:', commentId);
        return { success: false };
      }

      const newMedia = mediaOptions?.media;
      const hasNewLocalFile = !!(newMedia?.uri && isLocalMediaUri(newMedia.uri));
      const hasNewRemoteMedia = !!(newMedia?.uri && isRemoteMediaUri(newMedia.uri));
      const removeMedia = !!mediaOptions?.removeMedia && !hasNewLocalFile && !hasNewRemoteMedia;
      const keepMediaUrl =
        !hasNewLocalFile && !hasNewRemoteMedia && !removeMedia
          ? (mediaOptions?.existingMediaUrl || '').trim()
          : '';

      let contentTypeId = CommentContentType.Text;
      if (hasNewLocalFile || hasNewRemoteMedia) {
        if (
          newMedia!.mediaType === POST_MEDIA_TYPE.GIF ||
          newMedia!.uri.toLowerCase().endsWith('.gif') ||
          newMedia!.type?.includes('gif')
        ) {
          contentTypeId = CommentContentType.Gif;
        } else {
          contentTypeId = CommentContentType.Image;
        }
      } else if (keepMediaUrl) {
        contentTypeId =
          keepMediaUrl.toLowerCase().endsWith('.gif')
            ? CommentContentType.Gif
            : CommentContentType.Image;
      }

      const formData = new FormData();
      formData.append('PostCommentId', String(numCommentId));
      formData.append('ContentTypeId', String(contentTypeId));
      formData.append('BodyText', text || '');
      const mediaUrlValue = hasNewRemoteMedia
        ? (newMedia!.uri || '').trim()
        : keepMediaUrl;
      if (mediaUrlValue) {
        formData.append('MediaUrl', mediaUrlValue);
      }
      formData.append('DurationSeconds', '0');

      if (hasNewLocalFile) {
        formData.append('Files', {
          uri: newMedia!.uri,
          type: newMedia!.type || 'image/jpeg',
          name: newMedia!.fileName || 'upload.jpg',
        } as any);
      }

      const response = await feedApi.editComment(formData);
      if (response.status === 200 || response.data?.success) {
        return { success: true };
      }
      return { success: false };
    } catch (err) {
      console.error('[feedService] Error in editComment:', err);
      return { success: false };
    }
  },

  deleteComment: async (commentId: string): Promise<MutationResult> => {
    try {
      const response = await feedApi.deleteComment(Number(commentId));
      if (response.status === 200 || response.data?.success) {
        return { success: true };
      }
      return { success: false };
    } catch (err) {
      console.error('[feedService] Error in deleteComment:', err);
      return { success: false };
    }
  },


  /**
   * GetUserPosts leaves the author GUID off its rows, which strands the
   * Activities tab: those posts belong to other members, so the viewed
   * profile's GUID cannot stand in. GetPost returns the full feed row, so the
   * GUID is fetched on demand instead of guessed.
   */
  resolvePostAuthorGuid: async (postId: string): Promise<string | null> => {
    const normalizedPostId = normalizeNumericId(postId);
    // GetPost takes a JSON number, so an id past MAX_SAFE_INTEGER would be
    // rounded and fetch the wrong post — refuse rather than answer wrongly.
    if (!normalizedPostId || !Number.isSafeInteger(Number(normalizedPostId))) return null;
    try {
      const response = await feedApi.getPost({
        postId: Number(normalizedPostId),
        startIndex: 1,
        pageSize: 1,
      });
      if (!response.data?.success) return null;
      return resolveProfileUserGuidFromRecord(
        response.data.feed as unknown as Record<string, unknown>,
      );
    } catch {
      return null;
    }
  },

  getPostDetails: async (postId: string, startIndex: number = 1, pageSize: number = 10): Promise<{ success: boolean; data?: { post: FeedPost; comments: CommentItem[] } }> => {
    try {
      const numericPostId = parseInt(postId, 10);
      if (isNaN(numericPostId) || numericPostId <= 0) {
        console.warn('[feedService] getPostDetails called with non-numeric postId:', postId);
        return { success: false };
      }
      const response = await feedApi.getPost({
        postId: numericPostId,
        startIndex,
        pageSize,
      });
      if (response.status === 200 && response.data?.success) {
        const post = mapFeedApiResponse(response.data.feed);
        const comments = mapComments(response.data.comments || []);
        return { success: true, data: { post, comments } };
      }
      return { success: false };
    } catch (err) {
      console.error('[feedService] Error in getPostDetails:', err);
      return { success: false };
    }
  },

  getComments: async (postId: string, startIndex: number = 1, pageSize: number = 50): Promise<{ success: boolean; data: CommentItem[] }> => {
    try {
      const numericPostId = parseInt(postId, 10);
      if (isNaN(numericPostId) || numericPostId <= 0) {
        console.warn('[feedService] getComments called with non-numeric postId:', postId);
        return { success: false, data: [] };
      }
      const rawComments: CommentApiResponse[] = [];
      const seenIds = new Set<number>();
      let page = startIndex;
      const maxPages = 20;
      for (;;) {
        const response = await feedApi.getCommentsList({
          postId: numericPostId,
          startIndex: page,
          pageSize,
        });
        if (!(response.status === 200 && response.data?.success)) {
          if (rawComments.length === 0) return { success: false, data: [] };
          break;
        }
        const chunk = Array.isArray(response.data.data) ? response.data.data : [];
        let added = 0;
        for (const row of chunk) {
          if (seenIds.has(row.postCommentId)) continue;
          seenIds.add(row.postCommentId);
          rawComments.push(row);
          added += 1;
        }
        if (chunk.length < pageSize || added === 0) break;
        page += 1;
        if (page - startIndex >= maxPages) break;
      }
      return { success: true, data: mapComments(rawComments) };
    } catch (err) {
      console.error('[feedService] Error in getComments:', err);
      return { success: false, data: [] };
    }
  },

  toggleCommentLike: async (commentId: string, postCommentId?: number | string): Promise<MutationResult> => {
    try {
      const targetId = postCommentId != null && !isNaN(Number(postCommentId)) && Number(postCommentId) > 0
        ? Number(postCommentId)
        : Number(commentId);

      if (isNaN(targetId) || targetId <= 0) {
        console.warn('[feedService] toggleCommentLike called with invalid postCommentId:', commentId, postCommentId);
        return { success: false };
      }

      const response = await feedApi.commentReaction({
        postCommentId: targetId,
        reactionTypeId: 1, // Standard Like reaction type
      });
      if (response.status === 200 && response.data?.success !== false) {
        return { success: true };
      }
      return { success: false };
    } catch (err) {
      console.error('[feedService] Error in toggleCommentLike:', err);
      return { success: false };
    }
  },

  deletePost: async (postId: string): Promise<MutationResult> => {
    const normalizedPostId = normalizeNumericId(postId);
    if (!normalizedPostId) {
      console.error('[feedService] deletePost called with invalid postId:', postId);
      return { success: false, postId };
    }

    // Swagger: DELETE /Feeds/Post body `{ postId: number }`.
    // Never pass Number(invalid) — NaN serializes to JSON null and 400s/500s the API.
    const numericPostId = Number(normalizedPostId);
    if (!Number.isSafeInteger(numericPostId) || numericPostId <= 0) {
      console.error(
        '[feedService] deletePost postId is not a safe positive integer:',
        postId,
      );
      return { success: false, postId };
    }

    try {
      const response = await feedApi.deletePost({ postId: numericPostId });
      if (response.status === 200 || response.data?.success) {
        return { success: true, postId: normalizedPostId };
      }
      return { success: false, postId: normalizedPostId };
    } catch (err) {
      console.error('[feedService] Error in deletePost:', err);
      return { success: false, postId: normalizedPostId };
    }
  },

  updatePost: async (
    postId: string,
    payload: CreatePostPayload,
    profile: Profile | null,
    _t: TranslateFn,
  ): Promise<MutationResult<{ post: FeedPost }>> => {
    const normalizedPostId = normalizeNumericId(postId);
    if (!normalizedPostId) {
      console.error('[feedService] updatePost called with invalid postId:', postId);
      return { success: false };
    }

    try {
      const formData = buildPostFormData(payload, normalizedPostId);
      const response = await feedApi.editPost(formData);
      if (response.status === 200 || response.data?.success) {
        return {
          success: true,
          data: {
            post: buildOptimisticFeedPost(normalizedPostId, payload, profile),
          },
        };
      }
      return { success: false };
    } catch (err) {
      const apiErr = err as {
        message?: string;
        response?: { data?: { message?: string; statusCode?: number } };
      };
      console.error(
        '[feedService] Error in updatePost:',
        apiErr?.response?.data?.message || apiErr?.message || err,
        { postId: normalizedPostId, statusCode: apiErr?.response?.data?.statusCode },
      );
      return { success: false };
    }
  },

  createPost: async (
    payload: CreatePostPayload,
    profile: Profile | null,
    _t: TranslateFn,
  ): Promise<MutationResult<{ post: FeedPost }>> => {
    try {
      const formData = buildPostFormData(payload);
      const response = await feedApi.createPost(formData);
      if (
        (response.status === 201 || response.status === 200) &&
        response.data?.success !== false
      ) {
        const newPostId =
          extractPostIdFromResponse(response.data) ||
          // Last resort only when API omits id — still better than Date.now() for edits.
          extractPostIdFromResponse(response);
        if (!newPostId) {
          console.warn(
            '[feedService] createPost succeeded but response had no postId',
            response.data,
          );
        }
        const optimisticId =
          newPostId || `${PENDING_POST_ID_PREFIX}${Date.now()}`;
        return {
          success: true,
          data: {
            post: buildOptimisticFeedPost(optimisticId, payload, profile),
          },
        };
      }
      // A 200 that reports failure still carries the reason in the body.
      return { success: false, message: readResponseMessage(response.data) };
    } catch (err) {
      console.error('[feedService] Error in createPost:', err);
      return { success: false, message: getApiErrorMessage(err as ApiError) };
    }
  },

  getPostReactionList: async (postId: string, startIndex = 1, pageSize = 20): Promise<{ success: boolean; data: import('../feed/feedApi').PostReactionUser[] }> => {
    try {
      const numericPostId = Number(postId);
      const response = await feedApi.getPostReactionList({
        postId: numericPostId,
        startIndex,
        pageSize,
        reactionTypeId: 0,
      });
      if (response.status === 200 && (response.data?.success !== false)) {
        const users = response.data.data || response.data.reactions || [];
        return { success: true, data: users };
      }
      return { success: false, data: [] };
    } catch (err) {
      console.error('[feedService] Error in getPostReactionList:', err);
      return { success: false, data: [] };
    }
  },
};

export default feedService;
